import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';

const { FiUser, FiMail, FiMessageCircle, FiCreditCard, FiCalendar, FiRefreshCw, FiCheckCircle, FiSave, FiLoader } = FiIcons;

const STATUS_STYLES = {
  active: { label: 'Active', className: 'text-green-700 bg-green-100' },
  pending: { label: 'Pending Activation', className: 'text-amber-700 bg-amber-100' },
  expired: { label: 'Expired', className: 'text-red-700 bg-red-100' },
  cancelled: { label: 'Cancelled', className: 'text-gray-700 bg-gray-100' }
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

function Profile() {
  const { user, updateUser } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [whatsapp, setWhatsapp] = useState(user?.whatsapp || '');
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const status = STATUS_STYLES[user?.subscriptionStatus] || STATUS_STYLES.pending;

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSaved(false);
    setIsSaving(true);
    try {
      await api.updateProfile({ name, email, whatsapp });
      updateUser({ name, email, whatsapp });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err.message || 'Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-2xl mx-auto"
    >
      <div className="flex items-center space-x-3 mb-8">
        <div className="w-10 h-10 rounded-lg bg-[#19b48d] flex items-center justify-center">
          <SafeIcon icon={FiUser} className="text-white text-lg" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Your Profile</h2>
          <p className="text-gray-600">Contact details and subscription status</p>
        </div>
      </div>

      {/* Subscription (read-only) */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center space-x-2">
            <SafeIcon icon={FiCreditCard} className="text-[#19b48d]" />
            <h3 className="font-bold text-gray-900">Subscription</h3>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-bold ${status.className}`}>{status.label}</span>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-center space-x-1.5 text-xs text-gray-500 uppercase tracking-wide mb-1">
              <SafeIcon icon={FiCalendar} className="text-xs" />
              <span>Subscribed On</span>
            </div>
            <p className="text-sm font-bold text-gray-900">{formatDate(user?.subscriptionDate)}</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-center space-x-1.5 text-xs text-gray-500 uppercase tracking-wide mb-1">
              <SafeIcon icon={FiRefreshCw} className="text-xs" />
              <span>Renews On</span>
            </div>
            <p className="text-sm font-bold text-gray-900">{formatDate(user?.renewalDate)}</p>
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-4">
          Subscription status and dates are managed by iNVESTT — contact support if these need to change.
        </p>
      </div>

      {/* Contact details (editable) */}
      <form onSubmit={handleSave} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-5">
        <h3 className="font-bold text-gray-900 mb-1">Contact Details</h3>

        <div>
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Name</label>
          <div className="relative">
            <SafeIcon icon={FiUser} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full h-12 pl-11 pr-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#19b48d] focus:border-transparent outline-none font-medium text-gray-900"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Email</label>
          <div className="relative">
            <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full h-12 pl-11 pr-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#19b48d] focus:border-transparent outline-none font-medium text-gray-900"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">WhatsApp Number</label>
          <div className="relative">
            <SafeIcon icon={FiMessageCircle} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="tel"
              value={whatsapp}
              onChange={(e) => setWhatsapp(e.target.value)}
              placeholder="+91 98765 43210"
              className="w-full h-12 pl-11 pr-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#19b48d] focus:border-transparent outline-none font-medium text-gray-900"
            />
          </div>
        </div>

        <p className="text-[11px] text-gray-400">
          The number/email you log in with won't change even if you update this — contact support to change your login contact.
        </p>

        {error && (
          <div className="flex items-center space-x-2 text-red-500 text-sm font-medium bg-red-50 p-3 rounded-lg">
            <SafeIcon icon={FiIcons.FiAlertCircle} />
            <span>{error}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={isSaving}
          className="w-full h-12 bg-[#19b48d] text-white font-bold rounded-xl shadow-lg shadow-[#19b48d]/20 hover:bg-[#16a085] transition-all flex items-center justify-center space-x-2 disabled:opacity-70"
        >
          {isSaving ? (
            <SafeIcon icon={FiLoader} className="animate-spin" />
          ) : saved ? (
            <>
              <SafeIcon icon={FiCheckCircle} />
              <span>Saved</span>
            </>
          ) : (
            <>
              <SafeIcon icon={FiSave} />
              <span>Save Changes</span>
            </>
          )}
        </button>
      </form>
    </motion.div>
  );
}

export default Profile;